// TIDAK TERPAKAI — halaman V1, tidak terjangkau sejak App.js mengarahkan semua rute ke V2App. Jangan diedit. Lihat memory/DEPRECATED.md.
// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE
// version  : 1.0
// updated  : 2026-06-15
// replaces : -
// protected: NO  -> Tambahkan riwayat di /app/memory CHANGELOG saat diubah.
// =============================================================================

import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLiveRefresh } from "@/context/RealtimeContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatRupiah } from "@/lib/meta";
import { BarChart3, ShoppingBag, Tag, Gift, Undo2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const REGION_BY_ROLE = {
  KACAB_SBY: "SBY", KEPALA_STORE_AREMA: "SBY",
  KACAB_SMD: "SMD", KEPALA_STORE_BORNEO: "SMD",
};
const REGION_LABEL = { SBY: "Surabaya (Arema FC)", SMD: "Samarinda (Borneo FC)" };

export default function LaporanBulanan() {
  const { user } = useAuth();
  const now = new Date();
  const lockedRegion = REGION_BY_ROLE[user?.role];

  const [region, setRegion] = useState(lockedRegion || "SBY");
  const [mode, setMode] = useState("month"); // 'month' | 'range'
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { region, month: Number(month), year: Number(year) };
      if (mode === "range" && (startDate || endDate)) {
        params.start_date = startDate || undefined;
        params.end_date = endDate || undefined;
      }
      const r = await api.get("/reports/monthly/region", { params });
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memuat laporan"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [region, mode, month, year, startDate, endDate]);
  useLiveRefresh(["sales", "inventory", "invoices", "shipments", "returns"], () => load());

  const s = data?.summary || {};

  return (
    <div>
      <PageHeader title="Laporan Penjualan" subtitle="Laporan penjualan & hal terkait penjualan (diskon, compliment, retur) per wilayah. Stok & pemenuhan MoU tersedia di menu tersendiri." />

      {/* Filters */}
      <Card className="p-4 sm:p-5 rounded-2xl kpi-shadow mb-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
          <div>
            <Label>Wilayah</Label>
            <Select value={region} onValueChange={setRegion} disabled={!!lockedRegion}>
              <SelectTrigger data-testid="report-region-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="SBY">{REGION_LABEL.SBY}</SelectItem>
                <SelectItem value="SMD">{REGION_LABEL.SMD}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Memuat...</> : <>Periode: <b>{data?.period}</b></>}
          </div>
        </div>

        <div>
          <Label className="mb-1.5 block">Periode</Label>
          <RadioGroup value={mode} onValueChange={setMode} className="flex gap-2 mb-3" data-testid="report-mode-group">
            {[["month", "Bulanan"], ["range", "Rentang Tanggal"]].map(([v, l]) => (
              <Label key={v} className={`flex items-center gap-2 border rounded-xl px-3 py-2 text-sm cursor-pointer ${mode === v ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                <RadioGroupItem value={v} className="sr-only" />{l}
              </Label>
            ))}
          </RadioGroup>
          {mode === "month" ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <Label>Bulan</Label>
                <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                  <SelectTrigger data-testid="report-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tahun</Label><Input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="report-year-input" /></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label>Dari Tanggal</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="report-start-date" /></div>
              <div><Label>Sampai Tanggal</Label><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="report-end-date" /></div>
            </div>
          )}
        </div>
      </Card>

      {/* KPI summary (penjualan) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-5">
        <Kpi icon={ShoppingBag} label="Omzet" value={formatRupiah(s.omzet || 0)} />
        <Kpi icon={Tag} label="Qty Terjual" value={s.qty_terjual || 0} />
        <Kpi icon={BarChart3} label="Total Diskon" value={formatRupiah(s.total_diskon || 0)} sub={`ETAMS ${formatRupiah(s.beban_diskon_etams || 0)} / Tim ${formatRupiah(s.beban_diskon_tim || 0)}`} />
      </div>

      <Tabs defaultValue="terjual">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="terjual" data-testid="tab-terjual"><ShoppingBag className="w-4 h-4 mr-1" /> Terjual</TabsTrigger>
          <TabsTrigger value="diskon" data-testid="tab-diskon"><Tag className="w-4 h-4 mr-1" /> Item Diskon</TabsTrigger>
          <TabsTrigger value="compliment" data-testid="tab-compliment"><Gift className="w-4 h-4 mr-1" /> Compliment</TabsTrigger>
          <TabsTrigger value="retur" data-testid="tab-retur"><Undo2 className="w-4 h-4 mr-1" /> Retur</TabsTrigger>
        </TabsList>

        <TabsContent value="terjual">
          <DataTable head={["Invoice", "Tanggal", "Produk", "Size", "Harga", "Diskon", "Total"]} rows={(data?.terjual || []).map((r) => [r.invoice_no, r.tanggal, r.produk, r.size, formatRupiah(r.harga), r.diskon || "-", formatRupiah(r.line_total)])} empty="Belum ada penjualan" />
        </TabsContent>
        <TabsContent value="diskon">
          <div className="space-y-4 mt-3">
            <div>
              <h3 className="font-semibold text-sm mb-2 flex items-center gap-2"><Tag className="w-4 h-4" /> Rekap Diskon &amp; Beban Tim</h3>
              <DataTable head={["Jenis Diskon", "Qty", "Total Diskon", "Beban ETAMS", "Beban Tim"]}
                rows={(data?.diskon_recap || []).map((r) => [r.diskon, r.qty, formatRupiah(r.total_diskon), formatRupiah(r.beban_etams), formatRupiah(r.beban_tim)])}
                empty="Tidak ada diskon pada periode ini" />
            </div>
            <div>
              <h3 className="font-semibold text-sm mb-2">Detail Item Diskon</h3>
              <DataTable head={["Invoice", "Tanggal", "Produk", "Size", "Diskon", "Nominal", "Beban ETAMS", "Beban Tim"]}
                rows={(data?.item_diskon || []).map((r) => [r.invoice_no, r.tanggal, r.produk, r.size, r.diskon, formatRupiah(r.diskon_nominal), formatRupiah(r.beban_etams), formatRupiah(r.beban_tim)])}
                empty="Tidak ada item diskon" />
            </div>
          </div>
        </TabsContent>
        <TabsContent value="compliment">
          <DataTable head={["Invoice", "Tanggal", "Produk", "Size", "Untuk / Keterangan"]} rows={(data?.compliment || []).map((r) => [r.invoice_no, r.tanggal, r.produk, r.size, r.untuk || "-"])} empty="Tidak ada compliment" />
        </TabsContent>
        <TabsContent value="retur">
          <DataTable head={["No Retur", "Tanggal", "Qty", "Alasan", "Keterangan", "Status"]} rows={(data?.retur || []).map((r) => [r.return_no, r.tanggal, r.qty, r.alasan, r.keterangan || "-", r.status])} empty="Tidak ada retur" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, sub }) => (
  <Card className="p-4 rounded-2xl kpi-shadow">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon className="w-5 h-5" /></div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-xl font-semibold font-display">{value}</p>
        {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  </Card>
);

const DataTable = ({ head, rows, empty }) => (
  <Card className="rounded-2xl kpi-shadow overflow-hidden mt-1">
    <div className="max-h-[55vh] overflow-y-auto">
      <Table>
        <TableHeader><TableRow>{head.map((h, i) => <TableHead key={i}>{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.map((r, i) => <TableRow key={i}>{r.map((c, j) => <TableCell key={j} className={j === 0 ? "font-medium" : ""}>{c}</TableCell>)}</TableRow>)}
          {rows.length === 0 && <TableRow><TableCell colSpan={head.length} className="text-center py-10 text-muted-foreground">{empty}</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  </Card>
);
